import { ReactElement, useMemo } from 'react';
import { CampaignsView, campaignViewOptions, StageIndicators, viewFilterMap } from './types';
import { Badge, Button, ButtonGroup, MenuItem, MenuList, Stack } from '@ff-it/ui';
import { BlockStateTag } from 'modules/campaign/status/BlockStateTag';
import { Outlet, RouteObject, useRoutes } from 'react-router';
import { NavLink } from 'react-router-dom';
import useSWR from 'swr';
import { StageContainer } from './StageIndex';
import { stages } from './stages';

type CampaingsViewContainerProps = {
  view: CampaignsView;
  setView: (view: CampaignsView) => void;
};

const childStageKeys = new Set(Object.values(stages).flatMap((stage) => ('children' in stage ? stage.children : [])));
const parentAndStandaloneStages = Object.entries(stages).filter(
  ([key]) => !childStageKeys.has(key as keyof StageIndicators),
);

export function CampaingsViewContainer({ view, setView }: CampaingsViewContainerProps): ReactElement {
  const { data: indicators } = useSWR<StageIndicators>({
    url: `campaign/dashboard/indicators/${viewFilterMap[view]}/`,
    method: 'GET',
  });

  const routeConfig = useMemo<RouteObject[]>(() => {
    const routes: RouteObject[] = [
      {
        index: true,
        // @TODO: some kind of my jobs index
        element: <div />,
      },
    ];

    Object.entries(stages).forEach(([key, config]) => {
      if ('children' in config) {
        routes.push({
          path: key,
          element: (
            <>
              <MenuList direction="horizontal" variant="pills-inverse" size="sm">
                {config.children.map((childKey) => {
                  const childStage = stages[childKey];
                  return (
                    <MenuItem
                      key={childKey}
                      label={childStage.label}
                      component={NavLink}
                      to={childKey}
                      suffix={
                        indicators?.[childKey] ? <Badge variant="secondary" count={indicators[childKey]} /> : undefined
                      }
                    />
                  );
                })}
              </MenuList>
              <Outlet />
            </>
          ),
          children: config.children.map((childKey) => ({
            path: childKey,
            element: <StageContainer view={view} stage={childKey} />,
          })),
        });
      } else {
        routes.push({
          path: key,
          element: <StageContainer view={view} stage={key as keyof StageIndicators} />,
        });
      }
    });

    return routes;
  }, [view, indicators]);

  const routeElements = useRoutes(routeConfig);

  return (
    <Stack direction="vertical" mb="4">
      <Stack direction="horizontal" align="center">
        <MenuList direction="horizontal" variant="pills">
          {parentAndStandaloneStages.map(([key, config]) => {
            if ('children' in config) {
              const count = config.children.reduce((sum, childKey) => sum + (indicators?.[childKey] || 0), 0);
              const defaultChild =
                indicators &&
                config.children.reduce((prev, curr) =>
                  (indicators[curr] || 0) > (indicators[prev] || 0) ? curr : prev,
                );

              return (
                <MenuItem
                  key={key}
                  label={config.label}
                  prefix={<BlockStateTag state={config.blockState} iconOnly appearance="naked" />}
                  suffix={count ? <Badge variant="secondary" count={count} /> : undefined}
                  component={NavLink}
                  to={`${key}/${defaultChild || config.children[0]}`}
                />
              );
            }

            const indicatorKey = key as keyof StageIndicators;
            return (
              <MenuItem
                key={key}
                label={config.label}
                prefix={<BlockStateTag state={config.blockState} iconOnly appearance="naked" />}
                suffix={
                  indicators?.[indicatorKey] ? (
                    <Badge variant="secondary" count={indicators[indicatorKey]} />
                  ) : undefined
                }
                component={NavLink}
                to={key}
              />
            );
          })}
        </MenuList>
        <ButtonGroup ml="auto">
          {campaignViewOptions.map(({ value, label }) => (
            <Button key={value} active={value === view} onClick={() => setView(value)} size="sm">
              {label}
            </Button>
          ))}
        </ButtonGroup>
      </Stack>
      {routeElements}
    </Stack>
  );
}
